/**
 * CharacterPicker Component
 *
 * Optional step in challenge writing flow that allows
 * kids to pick a character from their library to use
 * in the challenge story.
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { User, ChevronDown, ChevronUp, Plus, X } from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { CompactCharacterCard } from '@/components/characters'
import { getCharactersForChild } from '@/services/characterLibraryService'
import type { LibraryCharacter } from '@/types/database'

interface CharacterPickerProps {
  childId: string
  selectedCharacterId: string | null
  onSelect: (character: LibraryCharacter | null) => void
  disabled?: boolean
}

export function CharacterPicker({
  childId,
  selectedCharacterId,
  onSelect,
  disabled = false,
}: CharacterPickerProps) {
  const [characters, setCharacters] = useState<LibraryCharacter[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isExpanded, setIsExpanded] = useState(!selectedCharacterId)
  const [selectedCharacter, setSelectedCharacter] = useState<LibraryCharacter | null>(null)

  // Load characters
  useEffect(() => {
    async function loadCharacters() {
      setIsLoading(true)
      try {
        const data = await getCharactersForChild(childId)
        setCharacters(data)

        // If there's a selected character ID, find it
        if (selectedCharacterId) {
          const selected = data.find(c => c.id === selectedCharacterId)
          setSelectedCharacter(selected || null)
        }
      } catch (error) {
        console.error('Error loading characters:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadCharacters()
  }, [childId, selectedCharacterId])

  const handleSelect = (character: LibraryCharacter) => {
    setSelectedCharacter(character)
    onSelect(character)
    setIsExpanded(false)
  }

  const handleClear = () => {
    setSelectedCharacter(null)
    onSelect(null)
    setIsExpanded(true)
  }

  // Don't show if no characters and loading is done
  if (!isLoading && characters.length === 0) {
    return (
      <Card className="mb-6 border-dashed border-2 border-[var(--color-kids-surface-alt)]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--color-kids-surface-alt)] flex items-center justify-center">
                <User className="w-5 h-5 text-[var(--color-kids-text-muted)]" />
              </div>
              <div>
                <p className="font-medium text-[var(--color-kids-text)]">
                  No characters yet
                </p>
                <p className="text-sm text-[var(--color-kids-text-muted)]">
                  Create a character to use in your stories!
                </p>
              </div>
            </div>
            <Link to="/characters/new">
              <Button variant="ghost" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Create
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="mb-6">
      <CardContent className="p-4">
        {/* Header - always visible */}
        <button
          onClick={() => !disabled && setIsExpanded(!isExpanded)}
          disabled={disabled}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            {selectedCharacter ? (
              <>
                <div className="w-10 h-10 rounded-full bg-[var(--color-kids-primary)]/10 flex items-center justify-center text-xl">
                  {selectedCharacter.avatar_emoji}
                </div>
                <div className="text-left">
                  <p className="font-medium text-[var(--color-kids-text)]">
                    Writing as {selectedCharacter.name}
                  </p>
                  <p className="text-sm text-[var(--color-kids-text-muted)]">
                    {(selectedCharacter.traits as string[])?.slice(0, 2).join(', ') || 'Your character'}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-[var(--color-kids-surface-alt)] flex items-center justify-center">
                  <User className="w-5 h-5 text-[var(--color-kids-text-muted)]" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-[var(--color-kids-text)]">
                    Pick a character (optional)
                  </p>
                  <p className="text-sm text-[var(--color-kids-text-muted)]">
                    Use one of your characters in this story
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {selectedCharacter && !disabled && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  handleClear()
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
            {!disabled && (
              isExpanded ? (
                <ChevronUp className="w-5 h-5 text-[var(--color-kids-text-muted)]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[var(--color-kids-text-muted)]" />
              )
            )}
          </div>
        </button>

        {/* Expandable character list */}
        <AnimatePresence>
          {isExpanded && !disabled && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="pt-4 border-t border-[var(--color-kids-surface-alt)] mt-4">
                {isLoading ? (
                  <div className="grid grid-cols-2 gap-3">
                    {[1, 2, 3, 4].map(i => (
                      <div
                        key={i}
                        className="h-20 bg-[var(--color-kids-surface-alt)] rounded-xl animate-pulse"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {characters.map(character => (
                      <CompactCharacterCard
                        key={character.id}
                        character={character}
                        selected={selectedCharacter?.id === character.id}
                        onClick={() => handleSelect(character)}
                      />
                    ))}

                    {/* Create new character option */}
                    <Link
                      to="/characters/new"
                      className="border-2 border-dashed border-[var(--color-kids-surface-alt)] rounded-xl p-3 flex items-center gap-2 hover:border-[var(--color-kids-primary)]/50 hover:bg-[var(--color-kids-primary)]/5 transition-all"
                    >
                      <div className="w-8 h-8 rounded-full bg-[var(--color-kids-surface-alt)] flex items-center justify-center">
                        <Plus className="w-4 h-4 text-[var(--color-kids-text-muted)]" />
                      </div>
                      <span className="text-sm text-[var(--color-kids-text-muted)]">
                        New character
                      </span>
                    </Link>
                  </div>
                )}

                {/* Skip button */}
                {selectedCharacter && (
                  <div className="mt-3 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClear}
                      className="text-[var(--color-kids-text-muted)]"
                    >
                      Write without a character
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  )
}
